import React, { useMemo, useState } from 'react'
import moment from 'moment'
import { SingleDatePicker, CalendarDay } from 'react-dates'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import {
  createIsDayBlockedForSelectablePaymentBusinessDates,
  createIsOutsideSelectablePaymentBusinessDates,
  isSelectablePaymentBusinessDate,
} from '@/libs/post-closing-sales-adjustment'

const DATE_VALUE_FORMAT = 'YYYY-MM-DD'

type PostClosingSelectableBusinessDatePickerProps = {
  date: moment.Moment | null
  onChange: (date: moment.Moment | null) => void
  selectablePaymentBusinessDates: Set<string>
  initialVisibleMonth?: (() => moment.Moment) | null
  inputId?: string
}

export const PostClosingSelectableBusinessDatePicker: React.FC<PostClosingSelectableBusinessDatePickerProps> = ({
  date,
  onChange,
  selectablePaymentBusinessDates,
  initialVisibleMonth,
  inputId = 'post-closing-business-date',
}) => {
  const { t } = useTranslation()
  const [calendarFocused, setCalendarFocused] = useState(false)
  const selectableDatesKey = useMemo(
    () =>
      Array.from(selectablePaymentBusinessDates)
        .sort()
        .join(','),
    [selectablePaymentBusinessDates],
  )

  const isOutsideRange = useMemo(() => createIsOutsideSelectablePaymentBusinessDates(selectablePaymentBusinessDates), [
    selectablePaymentBusinessDates,
  ])
  const isDayBlocked = useMemo(() => createIsDayBlockedForSelectablePaymentBusinessDates(selectablePaymentBusinessDates), [
    selectablePaymentBusinessDates,
  ])

  return (
    <SingleDatePicker
      key={selectableDatesKey}
      date={date}
      onDateChange={changeDate => {
        if (!changeDate) return
        const nextSalesDate = changeDate.format(DATE_VALUE_FORMAT)
        if (!isSelectablePaymentBusinessDate(nextSalesDate, selectablePaymentBusinessDates)) return
        onChange(changeDate)
      }}
      focused={calendarFocused}
      onFocusChange={v => setCalendarFocused(Boolean(v.focused))}
      numberOfMonths={1}
      isOutsideRange={isOutsideRange}
      isDayBlocked={isDayBlocked}
      enableOutsideDays={false}
      withPortal={true}
      initialVisibleMonth={initialVisibleMonth ?? undefined}
      displayFormat={t('YYYY-MM-DD')}
      monthFormat={t('YYYY-MM')}
      placeholder={t('YYYY-MM-DD')}
      id={inputId}
      hideKeyboardShortcutsPanel={true}
      customInputIcon={<img src={require('@/static/images/calendar_icon.svg')} width={14} />}
      navPrev={
        <div className="monthPrev">
          <img alt="arrow" src={require('@/static/images/icon_caalender_left.svg')} />
        </div>
      }
      navNext={
        <div className="monthNext">
          <img alt="arrow" src={require('@/static/images/icon_caalender_right.svg')} />
        </div>
      }
      renderCalendarDay={calendarDayProps => {
        const day = calendarDayProps.day
        const dayKey = day?.format(DATE_VALUE_FORMAT) ?? ''
        const isSelectable = Boolean(dayKey) && isSelectablePaymentBusinessDate(dayKey, selectablePaymentBusinessDates)

        return (
          <CalendarDay
            {...calendarDayProps}
            renderDayContents={renderDay => (
              <div css={isSelectable ? selectableDayStyle : blockedDayStyle}>
                <span>{renderDay?.format('D')}</span>
              </div>
            )}
          />
        )
      }}
    />
  )
}

const selectableDayStyle = css({
  display: 'block',
})

const blockedDayStyle = css({
  display: 'block',
  color: '#cacccd',
  cursor: 'not-allowed',
  pointerEvents: 'none',
})
